import { useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, AppState, Linking, Platform, StyleSheet, View } from 'react-native';
import { WebView } from 'react-native-webview';
import { UAEPassAuthResult } from '../types';

export interface UAEPassWebViewProps {
    authUrl: string | null;
    onAuthSuccess: (result: UAEPassAuthResult) => void;
    onAuthError: (error: string) => void;
    onClose: () => void;
    onCallbackData?: (code: string, state: string) => void;
}

/**
 * WebView component for UAE Pass authentication
 * Used as fallback when the UAE Pass app is not installed
 */
export const UAEPassWebView: React.FC<UAEPassWebViewProps> = ({
    authUrl,
    onAuthSuccess,
    onAuthError,
    onClose,
    onCallbackData
}) => {
    const c = useUIColors();
    const [isLoading, setIsLoading] = useState(true);
    const [currentUrl, setCurrentUrl] = useState<string | null>(authUrl);
    const webViewRef = useRef<WebView>(null);
    const processedCallbackRef = useRef(false);
    const currentUrlRef = useRef<string | null>(authUrl);
    const redirectUri = process.env.EXPO_PUBLIC_UAE_PASS_REDIRECT_URI;

    useEffect(() => {
        currentUrlRef.current = currentUrl || authUrl;
    }, [currentUrl, authUrl]);

    // Android Expo: WebView stays white after returning from the UAE Pass app.
    useEffect(() => {
        if (Platform.OS !== 'android') {
            return;
        }
        const subscription = AppState.addEventListener('change', (nextState) => {
            if (nextState !== 'active' || processedCallbackRef.current) {
                return;
            }
            const resumeUrl = currentUrlRef.current;
            if (!resumeUrl) {
                return;
            }
            setCurrentUrl(resumeUrl);
            webViewRef.current?.reload();
        });
        return () => {
            subscription.remove();
        };
    }, []);



    /**
     * Parse UAE Pass deep link URL and extract success/failure URLs
     * Following UAE Pass mobile app-to-app integration documentation
     */
    const parseUAEPassDeepLink = useCallback((url: string) => {
        try {
            // Always change scheme from 'uaepass' to 'uaepassstg' for staging
            let modifiedUrl = url.replace(/^uaepass:/, 'uaepassstg:');

            const urlObj = new URL(url);
            // UAE Pass uses lowercase parameter names: successurl and failureurl
            const successUrlParam = urlObj.searchParams.get('successurl');
            const failureUrlParam = urlObj.searchParams.get('failureurl');

            if (successUrlParam && failureUrlParam) {
                // Decode the URLs
                const decodedSuccessUrl = decodeURIComponent(successUrlParam);
                const decodedFailureUrl = decodeURIComponent(failureUrlParam);

                // Replace success and failure URLs with the configured redirect URI
                const appScheme = process.env.EXPO_PUBLIC_APP_SCHEME;
                if (!appScheme) {
                    logger.error('UAEPassWebView', 'EXPO_PUBLIC_APP_SCHEME is not set');
                    return modifiedUrl; // Return with scheme change at least
                }
                const wrappedSuccessUrl = `${appScheme}://uaepass?url=${encodeURIComponent(decodedSuccessUrl)}`;
                const wrappedFailureUrl = `${appScheme}://uaepass?url=${encodeURIComponent(decodedFailureUrl)}`;

                modifiedUrl = modifiedUrl.replace(
                    `successurl=${encodeURIComponent(successUrlParam)}`,
                    `successurl=${encodeURIComponent(wrappedSuccessUrl)}`
                ).replace(
                    `failureurl=${encodeURIComponent(failureUrlParam)}`,
                    `failureurl=${encodeURIComponent(wrappedFailureUrl)}`
                );

                logger.debug('UAEPassWebView', 'Stored Success URL:', decodedSuccessUrl);
            }

            logger.debug('UAEPassWebView', 'Original UAE Pass URL:', url);
            logger.debug('UAEPassWebView', 'Modified UAE Pass URL:', modifiedUrl);
            return modifiedUrl;
        } catch (error) {
            logger.error('UAEPassWebView', 'Error parsing UAE Pass deep link:', error);
            // Even on error, try to change the scheme
            return url.replace(/^uaepass:/, 'uaepassstg:');
        }
    }, []);

    /**
     * Handle UAE Pass deep link opening
     */
    const handleUAEPassDeepLink = useCallback((url: string) => {
        try {
            const modifiedUrl =
                Platform.OS === 'android' && url.startsWith('intent://')
                    ? url
                    : parseUAEPassDeepLink(url);
            logger.info('UAEPassWebView', 'UAE Pass deep link detected and modified:', modifiedUrl);
            Linking.openURL(modifiedUrl).then(() => {
                logger.info('UAEPassWebView', 'Successfully opened UAE Pass app with modified URL');
            }).catch(error => {
                logger.error('UAEPassWebView', 'Error opening UAE Pass app:', error);
                onAuthError('Failed to launch UAE Pass app');
            });
        } catch (error) {
            logger.error('UAEPassWebView', 'Error handling UAE Pass deep link:', error);
            onAuthError('Failed to launch UAE Pass app');
        }
    }, [parseUAEPassDeepLink, onAuthError]);

    /**
     * Handle callback URL by extracting code and state parameters
     */
    const handleCallbackUrl = useCallback((url: string) => {
        try {
            logger.debug('UAEPassWebView', 'Processing callback URL:', url);

            const urlObj = new URL(url);
            const code = urlObj.searchParams.get('code');
            const state = urlObj.searchParams.get('state');

            if (processedCallbackRef.current) {
                logger.debug('UAEPassWebView', 'Callback already processed, ignoring subsequent callback URL');
                return;
            }

            if (code && state && onCallbackData) {
                logger.debug('UAEPassWebView', 'Extracted callback data - code:', code, 'state:', state);
                processedCallbackRef.current = true;
                onCallbackData(code, state);
            } else {
                logger.error('UAEPassWebView', 'Missing code or state parameters in callback URL');
                onAuthError('Invalid callback parameters');
            }
        } catch (error) {
            logger.error('UAEPassWebView', 'Error parsing callback URL:', error);
            onAuthError('Failed to process authentication callback');
        }
    }, [onCallbackData, onAuthError]);

    // Listen for deep links to handle UAE Pass callback
    useEffect(() => {
        const handleDeepLink = (url: string) => {
            logger.debug('UAEPassWebView', 'Deep link received in UAE Pass WebView:', url);

            if (!url.startsWith(`${process.env.EXPO_PUBLIC_APP_SCHEME}://uaepass`)) {
                return;
            }

            try {
                const deepLinkUrl = new URL(url);

                // Check if this is a direct callback with code and state (user already logged in)
                const code = deepLinkUrl.searchParams.get('code');
                const state = deepLinkUrl.searchParams.get('state');

                if (code && state) {
                    // Direct callback with code/state - process immediately
                    logger.debug('UAEPassWebView', 'Direct callback detected with code and state');
                    handleCallbackUrl(url);
                    return;
                }

                // Otherwise, check for wrapped URL parameter (app-to-app flow)
                const urlParam = deepLinkUrl.searchParams.get('url');

                if (urlParam) {
                    const decodedUrl = decodeURIComponent(urlParam);
                    logger.debug('UAEPassWebView', 'Extracted URL from deep link:', decodedUrl);

                    // Load the extracted URL in the webview
                    setCurrentUrl(decodedUrl);
                    if (webViewRef.current) {
                        webViewRef.current.reload();
                    }
                }
            } catch (error) {
                logger.error('UAEPassWebView', 'Error parsing deep link:', error);
            }
        };

        // Check initial URL on mount (for Android cold start / activity recreate)
        Linking.getInitialURL().then((initialUrl) => {
            if (initialUrl) {
                handleDeepLink(initialUrl);
            }
        }).catch(() => undefined);

        // Listen for deep links
        const subscription = Linking.addEventListener('url', ({ url }) => {
            handleDeepLink(url);
        });

        // Cleanup
        return () => {
            subscription?.remove();
        };
    }, [handleCallbackUrl, setCurrentUrl]);

    /**
     * Handle WebView load requests
     * Processes navigation events and handles UAE Pass authentication flow
     */
    const handleShouldStartLoadWithRequest = useCallback((request: any) => {
        const { url } = request;

        // Check if this is a UAE Pass deep link (mobile app-to-app integration)
        if (
            url.startsWith('uaepass://') ||
            url.startsWith('uaepassstg://') ||
            (Platform.OS === 'android' && url.startsWith('intent://'))
        ) {
            // Handle UAE Pass URL opening
            handleUAEPassDeepLink(url);
            return false;
        }

        // Check if this is the callback URL
        if (redirectUri && url.startsWith(redirectUri)) {
            logger.debug('UAEPassWebView', 'Callback URL detected:', url);
            handleCallbackUrl(url);
            return false; // Don't proceed with navigation
        }

        // Allow all other URLs to load in the WebView
        logger.debug('UAEPassWebView', 'Navigation to:', url);
        return true;
    }, [handleUAEPassDeepLink, redirectUri, handleCallbackUrl]);

    /**
     * Handle WebView load start
     */
    const handleLoadStart = () => {
        setIsLoading(true);
    };

    /**
     * Handle WebView load end
     */
    const handleLoadEnd = () => {
        setIsLoading(false);
    };

    /**
     * Handle WebView errors
     */
    const handleError = (syntheticEvent: any) => {
        const { nativeEvent } = syntheticEvent;
        logger.error('UAEPassWebView', 'WebView error:', nativeEvent);

        // Ignore UAE Pass scheme errors as they are expected when redirecting to the app
        if (
            nativeEvent.url &&
            (nativeEvent.url.startsWith('uaepass://') ||
                nativeEvent.url.startsWith('uaepassstg://') ||
                (Platform.OS === 'android' && nativeEvent.url.startsWith('intent://')))
        ) {
            logger.debug('UAEPassWebView', 'Ignoring UAE Pass scheme error - this is expected behavior');
            return;
        }

        // Only report actual loading errors
        if (nativeEvent.code !== -10) { // -10 is ERR_UNKNOWN_URL_SCHEME
            onAuthError('Failed to load UAE Pass authentication page');
        }
    };

    /**
     * Handle HTTP errors
     */
    const handleHttpError = (syntheticEvent: any) => {
        const { nativeEvent } = syntheticEvent;
        logger.error('UAEPassWebView', 'WebView HTTP error:', nativeEvent);

        if (nativeEvent.statusCode >= 400) {
            onAuthError(`Authentication service error (${nativeEvent.statusCode})`);
        }
    };



    // Return early if no auth URL is provided
    if (!authUrl) {
        return null;
    }

    return (
        <View style={[styles.container, { backgroundColor: c.backgroundLevel1 }]}>
            {/* Loading Overlay */}
            {isLoading && (
                <View style={[styles.loadingContainer, { backgroundColor: c.backgroundLevel1 }]}>
                    <ActivityIndicator size="large" color={c.bloomRed} />
                </View>
            )}

            {/* WebView */}
            <WebView
                ref={webViewRef}
                source={{ uri: currentUrl || authUrl }}
                originWhitelist={
                    Platform.OS === 'android'
                        ? ['https://*', 'intent://*', 'uaepass://*', 'uaepassstg://*']
                        : ['https://', 'uaepass://', 'uaepassstg://']
                }
                onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
                onLoadStart={handleLoadStart}
                onLoadEnd={handleLoadEnd}
                onError={handleError}
                onHttpError={handleHttpError}
                style={styles.webView}
                javaScriptEnabled={true}
                domStorageEnabled={false}
                cacheEnabled={false}
                startInLoadingState={false}
                scalesPageToFit={true}
                allowsBackForwardNavigationGestures={true}
                setSupportMultipleWindows={false}
                // Security settings for UAE Pass
                mixedContentMode="compatibility"
                allowsInlineMediaPlayback={true}
                mediaPlaybackRequiresUserAction={false}
                // Platform-specific user agent for better compatibility
                userAgent={Platform.select({
                    ios: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.0 Mobile/15E148 Safari/604.1',
                    android: 'Mozilla/5.0 (Linux; Android 13; Mobile) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.0.0 Mobile Safari/537.36',
                    default: undefined as unknown as string,
                })}
                // Disable cookies and data storage for privacy
                thirdPartyCookiesEnabled={false}
                sharedCookiesEnabled={false}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    webView: {
        flex: 1,
    },
    loadingContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
    },
});

export default UAEPassWebView;